import type { PlaceFeature } from "~/lib/api";
import { cn } from "~/lib/utils";
import { Icon } from "~/components/ui/icons/icon";
import { PlaceStats } from "~/components/place/PlaceStats";
import placeholderImage from "~/assets/place-placeholder.webp";

interface FeaturedCardProps {
  place: PlaceFeature;
  isSelected: boolean;
  className?: string;
}

export function FeaturedCard({ place, isSelected, className }: FeaturedCardProps) {
  const { properties } = place;
  const { images, name, description } = properties;
  const imageUrl = images[0]?.large ?? placeholderImage;

  return (
    <div
      className={cn(
        "group relative w-full h-52 @sm:h-64 @3xl:h-96 rounded-2xl overflow-hidden transition-all duration-300",
        "ring-2 ring-transparent",
        { "ring-primary ring-offset-2 ring-offset-background": isSelected },
        className,
      )}
    >
      <div
        {...(images.length > 0 ? { role: "img", "aria-label": `Photo of ${name}` } : { "aria-hidden": true })}
        className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-105"
        style={{ backgroundImage: `url(${imageUrl})` }}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />

      <div className="absolute inset-x-0 bottom-0 p-5 text-left">
        <h3 className="text-base @sm:text-xl font-medium leading-tight text-white">{name}</h3>
        <PlaceStats
          properties={properties}
          compact
          outline
          className="mt-1.5 text-sm text-background/80"
          fallback={
            description ? <p className="mt-1.5 text-sm text-white/80 line-clamp-1">{description}</p> : undefined
          }
        />
      </div>

      <div
        className={cn(
          "absolute top-4 right-4 size-9 rounded-full flex items-center justify-center",
          "bg-white/20 backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity",
        )}
      >
        <Icon i="arrow-right" className="size-4 text-white" />
      </div>
    </div>
  );
}
